import { useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import type {
    CollectionKey,
    CollectionPlan
} from "@/components/usage-based-pricing-calculator/src/context";
import { UbpStep } from "@/components/usage-based-pricing-calculator/src/context";
import {
    useInputValues,
    useSetCollectionPlan,
    useStep
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { wacToken } from "@/libraries/design/foundations";

import { messages } from "./messages";

const sectionStyles = css({
    paddingBlock: token("space.400")
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const subtitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.300")
});

const planSelectorStyles = css({
    display: "inline-flex",
    alignItems: "center",
    gap: token("space.150"),
    padding: "12px 16px",
    backgroundColor: wacToken("color.extended.blue100"),
    border: `2px solid ${wacToken("color.primary.blue")}`,
    borderRadius: "12px",
    cursor: "pointer",
    marginBlockEnd: token("space.400")
});

const planIconStyles = css({
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    backgroundColor: wacToken("color.primary.blue"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
});

const planIconInnerStyles = css({
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    backgroundColor: wacToken("color.neutral.light0")
});

const planTextStyles = css({
    display: "flex",
    flexDirection: "column"
});

const planNameStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    color: wacToken("color.primary.black")
});

const planTierStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    lineHeight: "16px",
    color: wacToken("color.neutral.light700")
});

const selectStyles = css({
    appearance: "none",
    border: "none",
    background: "transparent",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    cursor: "pointer",
    color: wacToken("color.neutral.light700"),
    paddingInlineEnd: "4px"
});

const PLAN_MESSAGE_KEYS: Record<CollectionPlan, keyof typeof messages> = {
    free: "planFree",
    standard: "planStandard",
    premium: "planPremium",
    enterprise: "planEnterprise"
};

const PLAN_VALUES: CollectionPlan[] = [
    "free",
    "standard",
    "premium",
    "enterprise"
];

const COLLECTION_MESSAGE_KEYS: Record<CollectionKey, keyof typeof messages> = {
    teamwork: "teamworkCollection",
    strategy: "strategyCollection",
    service: "serviceCollection",
    software: "softwareCollection"
};

export const SolutionPlans = () => {
    const intl = useIntl();
    const step = useStep();
    const { collections } = useInputValues();
    const setCollectionPlan = useSetCollectionPlan();

    const selectedCollections = (
        Object.keys(collections) as CollectionKey[]
    ).filter((key) => collections[key].selected);

    const handlePlanChange = useCallback(
        (collectionKey: CollectionKey, plan: CollectionPlan) => {
            setCollectionPlan(collectionKey, plan);
        },
        [setCollectionPlan]
    );

    if (step < UbpStep.plans) {
        return null;
    }

    return (
        <div css={sectionStyles}>
            <Heading size="medium" as="h2">
                <span css={titleStyles}>
                    <FormattedMessage {...messages.solutionPlansTitle} />
                </span>
            </Heading>
            <p css={subtitleStyles}>
                <FormattedMessage {...messages.solutionPlansSubtitle} />
            </p>

            {selectedCollections.map((collectionKey) => (
                <div css={planSelectorStyles} key={collectionKey}>
                    <div css={planIconStyles}>
                        <div css={planIconInnerStyles} />
                    </div>
                    <div css={planTextStyles}>
                        <span css={planNameStyles}>
                            {intl.formatMessage(
                                messages[COLLECTION_MESSAGE_KEYS[collectionKey]]
                            )}
                        </span>
                        <span css={planTierStyles}>
                            {collections[collectionKey].plan
                                .charAt(0)
                                .toUpperCase() +
                                collections[collectionKey].plan.slice(1)}
                        </span>
                    </div>
                    <select
                        css={selectStyles}
                        value={collections[collectionKey].plan}
                        onChange={(e) =>
                            handlePlanChange(
                                collectionKey,
                                e.target.value as CollectionPlan
                            )
                        }
                        aria-label={intl.formatMessage(
                            messages.selectPlanLabel,
                            {
                                collectionName: intl.formatMessage(
                                    messages[
                                        COLLECTION_MESSAGE_KEYS[collectionKey]
                                    ]
                                )
                            }
                        )}
                    >
                        {PLAN_VALUES.map((planValue) => (
                            <option key={planValue} value={planValue}>
                                {intl.formatMessage(
                                    messages[PLAN_MESSAGE_KEYS[planValue]]
                                )}
                            </option>
                        ))}
                    </select>
                </div>
            ))}
        </div>
    );
};
